import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Share, SquarePlus, Smartphone, EllipsisVertical, Copy } from "lucide-react";
import {
  IOS_PUSH_MIN,
  PhoneSetup,
  StatusPill,
  browserPushState,
  detectPlatform,
  isApple,
  subscribeThisBrowser,
  thisBrowserSubscription,
  unsubscribeThisBrowser,
  type BrowserPushState,
  type Platform,
} from "@ozpanel/kit";
import { Button, Details, Section, cn, useConfirm, useIsPhone, useStandalone, useToast } from "@ozpanel/ui";
import { useMailboxApi } from "../api";
import { openInstalledApp, useAppInstalled, useInstallPrompt } from "../installPrompt";
import { useAppIdentity } from "../appIdentity";

// New-mail alerts, set up in the order the device needs — and only the steps THIS device needs.
//
// One card tried to serve a laptop, an Android phone and an iPhone at once (owner's screenshots,
// 2026-11-02): it showed a QR code saying "open it on your phone" to someone already holding the
// phone, buried the iPhone steps under it, and told an iPhone in Safari that "this browser cannot
// show notifications" — when the truth is that an iPhone shows them only from the home-screen app.
//
// What each device gets:
//   * iPhone/iPad in Safari: install first (Share → Add to Home Screen → open it from the icon),
//     then allow alerts THERE. Apple allows web alerts only in the installed app, from iOS 16.4.
//   * iPhone in another browser or inside a chat app: "open this in Safari", with the link to copy.
//   * Android: alerts work in the browser already; installing is offered, not required.
//   * A computer: alerts here, and a QR code to set up the phone — the only place a QR belongs.
//   * The installed app: no install card at all.

type DeviceState = "checking" | "on" | "off";

function deviceWord(p: Platform): string {
  return p.os === "ios" ? "iPhone" : p.os === "ipados" ? "iPad" : p.os === "android" ? "phone" : "computer";
}

/** This browser's subscription, read from the browser itself — the only place that knows. */
function useThisDevice(baseURL: string) {
  const [state, setState] = React.useState<DeviceState>("checking");
  const refresh = React.useCallback(async () => {
    try {
      const sub = await thisBrowserSubscription(baseURL);
      setState(sub && browserPushState() === "granted" ? "on" : "off");
    } catch {
      setState("off");
    }
  }, [baseURL]);
  React.useEffect(() => {
    void refresh();
  }, [refresh]);
  return { state, refresh };
}

export function InstallAndNotify() {
  const api = useMailboxApi();
  const qc = useQueryClient();
  const { notify } = useToast();
  const confirm = useConfirm();
  const isPhone = useIsPhone();
  const standalone = useStandalone();
  const platform = React.useMemo(() => ({ ...detectPlatform(), standalone }), [standalone]);
  const install = useInstallPrompt();
  const appInstalled = useAppInstalled();
  // The name the icon will carry: this domain's own app name where it has one.
  const identity = useAppIdentity();
  const baseURL = React.useMemo(() => new URL(import.meta.env.BASE_URL, window.location.origin).toString(), []);
  const device = useThisDevice(baseURL);
  const [perm, setPerm] = React.useState<BrowserPushState>(browserPushState);
  const [busy, setBusy] = React.useState(false);
  const app = useQuery({ queryKey: ["webmail-app-qr"], queryFn: () => api.webmailApp(), retry: false });

  const apple = isApple(platform);
  const appleTooOld = apple && platform.iosVersion != null && platform.iosVersion < IOS_PUSH_MIN;
  // An iPhone can only receive web alerts from the installed app: until then, push is "install first".
  const needsInstallFirst = apple && !platform.standalone;
  const word = deviceWord(platform);
  // On an iPhone in Safari the page is a numbered two-step flow; in another browser step 1 is
  // "open it in Safari", which is not numbered.
  const appleSteps = needsInstallFirst && (platform.browser === "safari" || platform.browser === "other");

  const turnOn = async () => {
    setBusy(true);
    try {
      const reached = await subscribeThisBrowser({
        baseURL,
        vapidKey: async () => (await api.vapidKey()).public_key,
        save: async (endpoint, keys, ua) => {
          await api.subscribePush(endpoint, keys, ua);
        },
      });
      setPerm(reached);
      if (reached !== "granted") {
        notify({
          kind: "error",
          title: "Alerts were not allowed",
          body: reached === "denied" ? "This browser is blocking alerts for this site — see below for how to allow them." : "No permission was given.",
        });
        return;
      }
      await device.refresh();
      void qc.invalidateQueries({ queryKey: ["push-subs"] });
      void qc.invalidateQueries({ queryKey: ["diagnostics"] });
      notify({ kind: "success", title: `Alerts are on for this ${word}` });
    } catch (e) {
      notify({ kind: "error", title: "Could not turn on alerts", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    try {
      await unsubscribeThisBrowser(baseURL);
      await device.refresh();
      void qc.invalidateQueries({ queryKey: ["push-subs"] });
      notify({ kind: "success", title: `Alerts are off for this ${word}` });
    } catch (e) {
      notify({ kind: "error", title: "Could not turn alerts off", body: e instanceof Error ? e.message : String(e) });
    }
  };

  const test = async () => {
    try {
      await api.testPush();
      notify({
        kind: "success",
        title: `Test sent — watch this ${word}`,
        body: "Nothing within a minute? Turn alerts off and on again here.",
      });
    } catch (e) {
      notify({ kind: "error", title: "Could not send a test", body: e instanceof Error ? e.message : String(e) });
    }
  };

  const link = app.data?.url ?? window.location.origin + import.meta.env.BASE_URL;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      notify({ kind: "success", title: "Link copied" });
    } catch {
      notify({ kind: "info", title: "Copy this address", body: link });
    }
  };

  // ── Install card ───────────────────────────────────────────────────────────────────────────────
  let installCard: React.ReactNode = null;
  if (!platform.standalone && appInstalled) {
    // Installed — just now, or the browser says so: the next step is opening it, not installing again.
    installCard = (
      <Section title="The app is installed">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="accent" onClick={() => openInstalledApp()}>
            <Smartphone size={16} aria-hidden /> Open the app
          </Button>
          <span className="text-[13px] text-muted">
            {isPhone
              ? `If it opens here in the browser instead, tap the ${identity.shortName} icon on your home screen or in your app list.`
              : `If it opens here in the browser instead, start ${identity.shortName} from your apps — it has its own window.`}
          </span>
        </div>
        <p className="mt-2 text-[12.5px] text-muted">It stays signed in. Turn on new-mail alerts in the app.</p>
      </Section>
    );
  } else if (!platform.standalone && !install.justInstalled) {
    if (apple && (platform.browser === "inapp" || (platform.browser !== "safari" && platform.browser !== "other"))) {
      installCard = (
        <Section title={`Install on your ${word}`}>
          <p className="text-[14px]">Open this page in <strong>Safari</strong> to install it.</p>
          <p className="mt-1 text-[13px] text-muted">On {word === "iPad" ? "an iPad" : "an iPhone"}, alerts work only in the app installed from Safari.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="accent" onClick={() => void copyLink()}>
              <Copy size={16} aria-hidden /> Copy the link
            </Button>
            <span className="min-w-0 font-mono text-[12.5px] break-all text-muted">{link}</span>
          </div>
          <Details summary="Why Safari?" className="mt-3">
            <p>
              Since iOS 16.4 some other browsers can add to the Home Screen through their Share menu too, but Safari is the one
              that always works. Links opened from a chat app open inside that app, which cannot install anything.
            </p>
          </Details>
        </Section>
      );
    } else if (apple) {
      installCard = (
        <Section title={`Step 1 · Install on your ${word}`}>
          <ol className="space-y-3 text-[14px]">
            <Step n={1}>
              Tap <Share size={17} aria-label="Share" className="mx-0.5 inline-block align-[-3px] text-accent" /> <strong>Share</strong>{" "}
              {word === "iPad" ? "at the top of Safari." : "at the bottom of Safari."}
            </Step>
            <Step n={2}>
              Choose <SquarePlus size={17} aria-hidden className="mx-0.5 inline-block align-[-3px] text-accent" /> <strong>Add to Home Screen</strong>,
              then <strong>Add</strong>.
            </Step>
            <Step n={3}>
              Open the <strong>{identity.shortName}</strong> icon on your Home Screen, and come back to this page there.
            </Step>
          </ol>
          {appleTooOld && (
            <p className="mt-3 rounded-lg border border-line bg-bg2 px-3 py-2 text-[13px] text-muted">
              This {word} runs iOS {platform.iosVersion?.toFixed(1)}. Mail alerts need iOS {IOS_PUSH_MIN} or later; the installed
              app still works without them.
            </p>
          )}
          <Details summary="Can't see Add to Home Screen?" className="mt-3">
            <p>Scroll down the Share sheet — it is below the row of apps. If it is still missing, tap Edit Actions and add it.</p>
          </Details>
        </Section>
      );
    } else if (platform.os === "android") {
      installCard = (
        <Section title="Install the app" hint="optional">
          <p className="text-[13.5px] text-muted">Opens like an app and stays signed in. Alerts work without it.</p>
          {platform.browser === "inapp" ? (
            <div className="mt-3 space-y-2">
              <p className="text-[14px]">
                Open this page in <strong>Chrome</strong> to install it.
              </p>
              <Button onClick={() => void copyLink()}>
                <Copy size={16} aria-hidden /> Copy the link
              </Button>
            </div>
          ) : install.prompt ? (
            <Button className="mt-3" variant="accent" onClick={() => void install.use()}>
              <Smartphone size={16} aria-hidden /> Install
            </Button>
          ) : (
            <ol className="mt-3 space-y-3 text-[14px]">
              {platform.browser === "samsung" ? (
                <>
                  <Step n={1}>Tap the menu (☰) at the bottom.</Step>
                  <Step n={2}>
                    Choose <strong>Add page to</strong> → <strong>Home screen</strong>.
                  </Step>
                </>
              ) : (
                <>
                  <Step n={1}>
                    Tap the menu <EllipsisVertical size={17} aria-label="(three dots)" className="inline-block align-[-3px] text-accent" /> at the top right.
                  </Step>
                  <Step n={2}>
                    Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.
                  </Step>
                </>
              )}
              <Step n={3}>Open it from the new icon from now on.</Step>
            </ol>
          )}
        </Section>
      );
    } else if (!isPhone) {
      // A computer: installing here is a convenience, the PHONE is where alerts matter most — and the
      // only place a QR code makes sense, because the person is not already holding the phone. The code
      // SIGNS THE PHONE IN (auth/pairing.go): it used to open the sign-in page, so the person still typed
      // the password on a phone keyboard (Oz, 2026-11-03: "logged in without having to enter a password").
      installCard = (
        <>
          <PhoneSetup path="/auth/pair" sessionsPath="/auth/sessions" title="Set up your phone" />
          {install.prompt && (
            <Section title="This computer" hint="optional">
              <Button onClick={() => void install.use()}>Install on this computer</Button>
            </Section>
          )}
        </>
      );
    }
  }

  // ── Alerts card ────────────────────────────────────────────────────────────────────────────────
  const on = device.state === "on";
  const pill = needsInstallFirst ? (
    <StatusPill tone="neutral">install first</StatusPill>
  ) : on ? (
    <StatusPill tone="ok">on</StatusPill>
  ) : perm === "denied" ? (
    <StatusPill tone="bad">blocked</StatusPill>
  ) : perm === "unsupported" ? (
    <StatusPill tone="neutral">not available</StatusPill>
  ) : (
    <StatusPill tone="warn">off</StatusPill>
  );

  let alertsBody: React.ReactNode;
  if (needsInstallFirst) {
    alertsBody = (
      <p className="text-[13.5px] text-muted">
        {appleTooOld
          ? `Mail alerts need iOS ${IOS_PUSH_MIN} or later.`
          : appleSteps
            ? "Install the app (step 1), open it from the icon, then turn alerts on there."
            : "Open this page in Safari and install it first; then turn alerts on in the installed app."}
      </p>
    );
  } else if (perm === "unsupported") {
    alertsBody = (
      <p className="text-[13.5px] text-muted">
        This browser cannot show alerts from a website. Your mail still arrives normally, and mail apps have their own alerts.
      </p>
    );
  } else if (perm === "denied") {
    alertsBody = (
      <div className="space-y-2 text-[13.5px]">
        <p className="text-muted">This {word} is blocking alerts for this app. Allow them, then come back:</p>
        <p>
          {apple
            ? "Settings → Notifications → find this app → Allow Notifications."
            : platform.os === "android"
              ? platform.standalone
                ? "Press and hold the app icon → App info → Notifications → allow."
                : "Tap the icon left of the address → Permissions → Notifications → Allow."
              : "Click the icon left of the address → Site settings → Notifications → Allow."}
        </p>
      </div>
    );
  } else if (on) {
    alertsBody = (
      <div className="space-y-3">
        <p className="text-[13.5px] text-muted">New mail alerts this {word}.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="accent" onClick={() => void test()}>
            Send a test
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              confirm.ask(
                {
                  title: `Turn off alerts for this ${word}?`,
                  body: "New mail stops alerting here. Your other devices are not affected.",
                  confirmLabel: "Turn off",
                  destructive: false,
                },
                turnOff,
              )
            }
          >
            Turn off here
          </Button>
        </div>
      </div>
    );
  } else {
    alertsBody = (
      <div className="space-y-3">
        <p className="text-[13.5px] text-muted">Get an alert on this {word} when new mail arrives.</p>
        <Button variant="accent" disabled={busy || device.state === "checking"} onClick={() => void turnOn()}>
          {busy ? "Setting up…" : perm === "granted" ? "Turn on alerts" : "Allow alerts"}
        </Button>
      </div>
    );
  }

  return (
    <>
      {confirm.dialog}
      {installCard}
      <Section title={appleSteps ? "Step 2 · New-mail alerts" : "New-mail alerts"} actions={pill}>
        <div className={cn(needsInstallFirst && "opacity-90")}>{alertsBody}</div>
        <Details summary="One device at a time" className="mt-3">
          <p>Alerts are turned on separately on each phone and computer. The devices that receive them are listed below.</p>
        </Details>
      </Section>
    </>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span aria-hidden className="grid size-6 shrink-0 place-items-center rounded-full bg-accentsoft text-[12.5px] font-semibold text-accent">
        {n}
      </span>
      <span className="min-w-0 pt-0.5">{children}</span>
    </li>
  );
}
